import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { SectionHeading } from '../components/shared/SectionHeading';

const CookiesPage: React.FC = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`cookiesPage.sections.${key}`);
  const functionalRows = t('cookiesPage.sections.overview.functional.rows', { returnObjects: true }) as string[][];
  const tableHeaders = t('cookiesPage.sections.overview.functional.tableHeaders', { returnObjects: true }) as string[];
  const browsers = t('cookiesPage.sections.browser.browsers', { returnObjects: true }) as string[];

  return (
    <div className="page-shell">
      <Header />
      <section className="section-shell">
        <div className="section-container">
          <div className="surface-card-soft p-8 md:p-10">
            <Link to="/" className="mb-6 inline-block text-sm text-slate-500 hover:text-primary hover:underline">
              {t('cookiesPage.backToHome')}
            </Link>
            <SectionHeading
              eyebrow={t('cookiesPage.title')}
              title={t('cookiesPage.title')}
              subtitle={t('cookiesPage.lastUpdated')}
            />
          </div>
        </div>
      </section>

      <div className="section-container pb-20">
        <div className="mx-auto max-w-4xl space-y-6">
        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('what.title')}</h2>
          <p className="leading-relaxed text-slate-700">{s('what.text')}</p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('overview.title')}</h2>

          <div className="mb-8">
            <h3 className="mb-2 text-lg font-semibold text-ink">{s('overview.functional.title')}</h3>
            <p className="mb-3 text-sm text-slate-600">{s('overview.functional.description')}</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-primary/10">
                    {Array.isArray(tableHeaders) && tableHeaders.map((h, i) => (
                      <th key={i} className="p-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary/10">
                  {Array.isArray(functionalRows) && functionalRows.map((row, i) => (
                    <tr key={i}>
                      <td className="p-3 font-mono text-xs">{row[0]}</td>
                      <td className="p-3 text-slate-700">{row[1]}</td>
                      <td className="p-3 text-slate-600">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="mb-2 text-lg font-semibold text-ink">{s('overview.analytics.title')}</h3>
            <p className="text-sm text-slate-600">{s('overview.analytics.description')}</p>
          </div>

          <div>
            <h3 className="mb-2 text-lg font-semibold text-ink">{s('overview.marketing.title')}</h3>
            <p className="text-sm text-slate-600">{s('overview.marketing.description')}</p>
          </div>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('consent.title')}</h2>
          <p className="leading-relaxed text-slate-700">{s('consent.text')}</p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('browser.title')}</h2>
          <p className="mb-3 leading-relaxed text-slate-700">{s('browser.text')}</p>
          <ul className="list-disc space-y-1 pl-6 text-sm text-slate-700">
            {Array.isArray(browsers) && browsers.map((browser, i) => (
              <li key={i}>{browser}</li>
            ))}
          </ul>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('moreInfo.title')}</h2>
          <p className="leading-relaxed text-slate-700">
            {s('moreInfo.text').split(s('moreInfo.privacyLink'))[0]}
            <Link to="/privacy" className="text-primary underline">{s('moreInfo.privacyLink')}</Link>
            {s('moreInfo.text').split(s('moreInfo.privacyLink'))[1] || ''}
          </p>
        </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CookiesPage;
